import IFileService from "data/IFileService";
import { TerminationOfExistingContract } from "features/contracting/enums/terminationOfExistingContract";
import {
  getBoolText,
  getFormattedDate,
  getTypedStepValues,
} from "features/contracting/helpers/contractingFormHelper";
import ContractingStepSummaryProps from "features/contracting/models/ContractingStepSummaryProps";
import ProcessStep from "features/contracting/models/steps/process/ProcessStep";
import {
  isFilledProcessType,
  isFixedTermProlongation,
  isInvoiceUploadType,
} from "features/contracting/sections/contractingForms/processForm/processFormHelper";
import PageCard from "features/shared/components/card/PageCard";
import CreatableItem from "features/shared/components/creatableItem/CreatableItem";
import CheckValueInfoItem from "features/shared/components/infoItem/CheckValueInfoItem";
import InfoItemInline from "features/shared/components/infoItem/InfoItemInline";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";

const ProcessSummary = ({
  contracting,
  step,
  configuration,
}: ContractingStepSummaryProps) => {
  // translations
  const tBool = useTranslation("shared\\enums\\boolEnum").t;

  const tTypes = useTranslation(
    "contracting\\enums\\terminationOfExistingContract"
  ).t;

  const tSupplierTypes = useTranslation(
    "contracting\\enums\\currentSupplierType"
  ).t;

  const tYesSubForm = useTranslationWithPrefix("contracting\\contracting", "yesTerminationByUsSubForm");
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "processSummary");

  //  DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);

  // private props
  const values = getTypedStepValues<ProcessStep>(contracting, step.component);

  // handlers
  const downloadUploadedFile = (code: string) => {
    if (values.currentSupplierInvoice) {
      fileService.downloadBase64Document(values.currentSupplierInvoice);
    }
  };

  // helpers
  const equalProccesType = (processType: TerminationOfExistingContract) => {
    return processType === values.terminationOfExistingContract;
  };

  const getSupplier = (value: string) => {
    const supplier = configuration.suppliers?.find((x) => x.code === value);
    return supplier?.name ? supplier.name : "-";
  };

  const tDurationType = (value: string) => {
    return tYesSubForm(`contractDurationType.${value}`);
  };

  const getScheme = (value: string) => {
    const supplier = configuration.terminationSchemes?.find(
      (x) => x.code === value
    );
    return supplier?.name ? supplier.name : "-";
  };

  // sub-renders
  const renderAnswerTitle = () => {
    const answer = values.terminationOfExistingContract as string;

    if (answer && answer !== "Undefined") {
      return (
        <div className="font-weight-bold">
          {tTypes(`${answer}.title`)}
        </div>
      );
    } else {
      return <div>-</div>;
    }
  };

  const renderUploadedFile = () => {
    if (values.currentSupplierInvoice) {
      return (
        <CreatableItem
          text={values.currentSupplierInvoice.fileNameWithExtension!}
          code={"uploadedInvoice"}
          actionIcon="download"
          onActionClick={downloadUploadedFile}
        />
      );
    } else {
      return <div>-</div>;
    }
  };

  const renderSupplier = () => {
    const supplierCode = values.noContractByAnother?.currentSupplierCode;
    const supplierType = values.noContractByAnother?.currentSupplierType;

    if (supplierCode && supplierType) {
      return (
        <div className="text-right">
          <div className="font-weight-bold">{getSupplier(supplierCode)}</div>
          <div className="small text-muted">
            {tSupplierTypes(`${supplierType}.subtitle`)}
          </div>
        </div>
      );
    } else {
      return <div>-</div>;
    }
  };

  const renderNoPlaceOfDeliveryInfo = (
    existsConnectionContract: boolean | undefined,
    ownerOfProperty: boolean | undefined
  ) => {
    return (
      <PageCard
        title={tLabels("noPlaceOfDeliveryInfoCardTitle")}
        bodyClassName="px-4 py-3"
      >
        <CheckValueInfoItem
          title={tLabels("existsConnectionContract")}
          value={existsConnectionContract}
          formatValue={(x) => getBoolText(x, tBool)}
          isEmpty={_.isUndefined}
        />
        <CheckValueInfoItem
          title={tLabels("ownerOfProperty")}
          value={ownerOfProperty}
          formatValue={(x) => getBoolText(x, tBool)}
          isEmpty={_.isUndefined}
        />
      </PageCard>
    );
  };

  // render
  return (
    <>
      <PageCard
        title={tLabels("terminationOfExistingContractCardTitle")}
        bodyClassName="px-4 py-3"
      >
        <div className="row no-gutters justify-content-between align-items-center">
          <div className="col-sm-auto text-muted pr-3">{tLabels("answer")}</div>
          <div className="col-sm text-sm-right">
            {renderAnswerTitle()}
            {values.terminationOfExistingContract && (
              <div className="small text-muted">
                {tTypes(`${values.terminationOfExistingContract}.subtitle`)}
              </div>
            )}
          </div>
        </div>
      </PageCard>

      {/* Answer - YesTerminationByUs */}
      <RenderWrapper isVisible={() => equalProccesType("YesTerminationByUs")}>
        <PageCard title={tLabels("currentSupplierCardTitle")} bodyClassName="px-4 py-3">
          <CheckValueInfoItem
            title={tLabels("currentSupplierCode")}
            value={values.yesTerminationByUs?.currentSupplierCode}
            formatValue={getSupplier}
          />
        </PageCard>

        <PageCard
          title={tLabels("contractDurationTypeCodeCardTitle")}
          bodyClassName="px-4 py-3"
        >
          <CheckValueInfoItem
            title={tLabels("contractDurationTypeCode")}
            value={values.yesTerminationByUs?.contractDurationTypeCode}
            formatValue={tDurationType}
          />
          <CheckValueInfoItem
            title={tLabels("terminationSchemeCode")}
            value={values.yesTerminationByUs?.terminationSchemeCode}
            formatValue={getScheme}
          />
          <RenderWrapper
            isVisible={() =>
              isFixedTermProlongation(
                values.yesTerminationByUs?.contractDurationTypeCode
              )
            }
          >
            <CheckValueInfoItem
              title={tLabels("contractEffectiveDate")}
              value={values.yesTerminationByUs?.contractEffectiveDate}
              formatValue={getFormattedDate}
            />
          </RenderWrapper>
        </PageCard>
      </RenderWrapper>

      {/* Answer - NoFixedContratDuration */}
      <RenderWrapper
        isVisible={() => equalProccesType("NoFixedContratDuration")}
      >
        <PageCard
          title={tLabels("contractEffectiveDateCardTitle")}
          bodyClassName="px-4 py-3"
        >
          <CheckValueInfoItem
            title={tLabels("contractEffectiveDate")}
            value={values.noFixedContratDuration?.contractEffectiveDate}
            formatValue={getFormattedDate}
          />
        </PageCard>
      </RenderWrapper>

      {/* Answer - NoContractByAnother */}
      <RenderWrapper isVisible={() => equalProccesType("NoContractByAnother")}>
        <PageCard title={tLabels("currentSupplierCardTitle")} bodyClassName="px-4 py-3">
          <InfoItemInline
            title={tLabels("currentSupplierCode")}
            value={renderSupplier()}
            valueBold={false}
            className="align-items-center"
          />
        </PageCard>
      </RenderWrapper>

      {/* Answer - NoPlaceOfDeliveryNotConnected */}
      <RenderWrapper
        isVisible={() => equalProccesType("NoPlaceOfDeliveryNotConnected")}
      >
        {renderNoPlaceOfDeliveryInfo(
          values.noPlaceOfDeliveryNotConnected?.existsConnectionContract,
          values.noPlaceOfDeliveryNotConnected?.ownerOfProperty
        )}
      </RenderWrapper>

      {/* Answer - NoPlaceOfDeliveryNotConnected */}
      <RenderWrapper
        isVisible={() => equalProccesType("NoPlaceOfDeliveryNotExists")}
      >
        {renderNoPlaceOfDeliveryInfo(
          values.noPlaceOfDeliveryNotExists?.existsConnectionContract,
          values.noPlaceOfDeliveryNotExists?.ownerOfProperty
        )}
      </RenderWrapper>

      {/* Shared info */}
      <RenderWrapper
        isVisible={() =>
          isFilledProcessType(values.terminationOfExistingContract!)
        }
      >
        <PageCard
          title={tLabels("contractExpectedEffectiveDateCardTitle")}
          bodyClassName="px-4 py-3"
        >
          <CheckValueInfoItem
            title={tLabels("contractExpectedEffectiveDate")}
            value={values.contractExpectedEffectiveDate}
            formatValue={getFormattedDate}
          />
        </PageCard>

        <RenderWrapper
          isVisible={() =>
            isInvoiceUploadType(values.terminationOfExistingContract!)
          }
        >
          <PageCard
            title={tLabels("currentSupplierInvoiceCardTitle")}
            bodyClassName="px-4 py-3"
          >
            <InfoItemInline
              title={tLabels("currentSupplierInvoice")}
              value={renderUploadedFile()}
              valueBold={false}
              className="align-items-center"
            />
          </PageCard>
        </RenderWrapper>
      </RenderWrapper>
    </>
  );
};

export default ProcessSummary;
